import {
  DELETE_TARIFIED_ITEM_ERROR,
  DELETE_TARIFIED_ITEM_START,
  DELETE_TARIFIED_ITEM_SUCCESS,
  FINISH_TARIFICATION_SESSION_ERROR,
  FINISH_TARIFICATION_SESSION_START,
  FINISH_TARIFICATION_SESSION_SUCCESS,
  TARIFICATION_ETAR_ERROR,
  TARIFICATION_ETAR_START,
  TARIFICATION_ETAR_SUCCESS,
  TARIFICATION_SESSION_APP_ERROR,
  TARIFICATION_SESSION_APP_START,
  TARIFICATION_SESSION_APP_SUCCESS,
  TARIFICATION_SESSION_DELETE_ERROR,
  TARIFICATION_SESSION_DELETE_START,
  TARIFICATION_SESSION_DELETE_SUCCESS,
  TARIFICATION_SESSION_DETAILS_SUCCESS,
  TARIFICATION_SESSION_ERROR,
  TARIFICATION_SESSION_START,
  TARIFICATION_SESSION_SUCCESS,
  TarificationAction,
  UPDATE_TARIFIED_ITEM_ERROR,
  UPDATE_TARIFIED_ITEM_START,
  UPDATE_TARIFIED_ITEM_SUCCESS,
} from "store/types/tarification.types";
import { Dispatch } from "redux";
import * as TarificationApi from "api/tarification.api";
import { TarificationSessionKey } from "model/tarification-session-key.model";
import { ETarResult } from "model/e-tar.result";
import { PaymentMethodEnum } from "common/enums/PaymentMethodEnum";
import { Attestation } from "model/attestion.model";
import { History } from "history";
import {
  ROUTE_TARIFICATION_COPAY,
  ROUTE_TARIFICATION_NOM,
} from "routes.global";
import { dispatchTarificationUserSettingsLoaded } from "store/actions/user-context.actions";
import { UserContextActionTypes } from "store/types/user-context.types";
import { TarificationApplicationSession } from "model/tarification.model";
import * as HelenaActions from "store/actions/helena.actions";
import { HelenaSessionKey } from "model/helena.model";
import { ThunkAction } from "redux-thunk";
import { GlobalState } from "store/configure-store";
import { ErrorTypeEnum } from "model/error.model";
import {
  PatientAction,
  TARIFIED_TODAY,
  TARIFIED_TODAY_START,
} from "store/types/patient.types";

/** ---- ACTIONS ---- */
export const createTarificationApplicationSession = (
  userId: number,
  stsToken: string,
  eHealthPassword: string
) => {
  return async (
    dispatch: Dispatch<TarificationAction | UserContextActionTypes>
  ) => {
    try {
      dispatchTarificationApplicationSessionStart()(dispatch);
      const session = await TarificationApi.getTarificationApplicationSession(
        userId,
        stsToken,
        eHealthPassword
      );
      dispatchTarificationApplicationSessionSuccess(session)(dispatch);
      const useEAttest = await TarificationApi.loadUserSettings(
        userId,
        session
      );
      dispatchTarificationUserSettingsLoaded(useEAttest)(dispatch);
    } catch (e) {
      dispatchTarificationApplicationSessionError()(dispatch);
    }
  };
};

export const updateTarifiedItemAmount = (
  session: TarificationApplicationSession,
  userId: number,
  sessionId: string,
  tarifiedItemId: string,
  amount: number,
  attestation: Attestation
) => {
  return async (dispatch: Dispatch<TarificationAction>) => {
    try {
      dispatchUpdateTarifiedItemStart()(dispatch);
      const tSession = await TarificationApi.updateTarifiedItemAmount(
        session,
        userId,
        sessionId,
        tarifiedItemId,
        amount
      );
      dispatchUpdateTarifiedItemSuccess(tSession)(dispatch);
      await checkOptionalETar(session, userId, attestation, tSession)(dispatch);
    } catch (e) {
      dispatchUpdateTarifiedItemError()(dispatch);
    }
  };
};

export const deleteTarifiedItemAction = (
  session: TarificationApplicationSession,
  userId: number,
  sessionId: string,
  tarifiedItemId: string
) => {
  return async (dispatch: Dispatch<TarificationAction>) => {
    try {
      dispatchDeleteTarifiedItemStart()(dispatch);
      await TarificationApi.deleteTarifiedItem(
        session,
        userId,
        sessionId,
        tarifiedItemId
      );
      dispatchDeleteTarifiedItemSuccess(tarifiedItemId)(dispatch);
      dispatchTarificationSessionStart()(dispatch);
      const newSession = await TarificationApi.tarificationSessionDetails(
        session,
        userId,
        sessionId
      );
      dispatchTarificationSessionDetailsSuccess(newSession)(dispatch);
      return await checkETarAction(session, sessionId, userId)(dispatch);
    } catch (e) {
      dispatchDeleteTarifiedItemError()(dispatch);
    }
  };
};

export const checkETarAction = (
  session: TarificationApplicationSession,
  tSessionId: string,
  userId: number
) => {
  return async (dispatch: Dispatch<TarificationAction>) => {
    dispatchCheckETarStart()(dispatch);
    try {
      const response = await TarificationApi.checkETar(
        session,
        tSessionId,
        userId
      );
      return dispatchCheckETarSuccess(response)(dispatch);
    } catch (e) {
      return dispatchCheckETarError()(dispatch);
    }
  };
};

export const createTarificationSession = (
  session: TarificationApplicationSession,
  userId: number,
  patientId: number,
  attestation: Attestation,
  history: History,
  tSession?: TarificationSessionKey
) => {
  return async (dispatch: Dispatch<TarificationAction>) => {
    try {
      dispatchTarificationSessionStart()(dispatch);
      await deleteOptionalTarificationSession(
        session,
        userId,
        patientId,
        tSession
      )(dispatch);
      let newSession = await TarificationApi.getTarificationSession(
        session,
        userId,
        patientId,
        attestation
      );
      dispatchTarificationSessionSuccess(newSession)(dispatch);
      await checkOptionalETar(
        session,
        userId,
        attestation,
        newSession
      )(dispatch);
      return;
    } catch (e) {
      dispatchTarificationSessionError()(dispatch);
      history.replace(ROUTE_TARIFICATION_COPAY);
    }
  };
};

export type FinishTarificationSessionType = (
  session: TarificationApplicationSession,
  tSession: TarificationSessionKey,
  userId: number,
  payedAmount: number,
  paymentMethod: PaymentMethodEnum,
  eAttest: boolean,
  history: History,
  patientId: number,
  helenaSessionKey: HelenaSessionKey | null,
  helenaPatientId: string | null,
  canUseHelena: boolean
) => Promise<string | undefined>;

export const tarifiedEAttestToday = (
  session: TarificationApplicationSession,
  userId: number,
  patientId: number
): ThunkAction<Promise<void>, GlobalState, any, TarificationAction> => {
  return async (dispatch) => {
    dispatch(dispatchCheckTarifiedTodayStart());
    const itemsToday = await TarificationApi.getTarifiedEAttestItemsToday(
      session,
      userId,
      patientId
    );

    dispatch(dispatchCheckTarifiedToday(itemsToday.length > 0));
  };
};

export const finishTarificationSession = (
  session: TarificationApplicationSession,
  tSession: TarificationSessionKey,
  userId: number,
  payedAmount: number,
  paymentMethod: PaymentMethodEnum,
  eAttest: boolean,
  history: History,
  patientId: number,
  helenaSessionKey: HelenaSessionKey | null,
  helenaPatientId: string | null,
  canUseHelena: boolean
): ThunkAction<
  Promise<string | undefined>,
  GlobalState,
  any,
  TarificationAction
> => {
  return async (dispatch) => {
    dispatchFinishTarificationSessionStart()(dispatch);
    let pdf;
    try {
      await TarificationApi.finishTarification(
        session,
        tSession.tarificationSessionId,
        userId,
        payedAmount,
        paymentMethod,
        eAttest
      );
      if (canUseHelena) {
        pdf = atob(await TarificationApi.getReceipt(session, tSession, userId));
        await dispatch(
          HelenaActions.createAttestationHE(
            patientId,
            userId,
            helenaSessionKey,
            helenaPatientId,
            pdf
          )
        );
      }
      dispatchFinishTarificationSessionSuccess()(dispatch);
    } catch (e) {
      if (e.type === ErrorTypeEnum.ATTESTATION_HELENA) {
        return pdf;
      } else {
        dispatchFinishTarificationSessionError(e)(dispatch);
        history.replace(ROUTE_TARIFICATION_NOM);
      }
    }
  };
};

export const deleteOptionalTarificationSession = (
  session: TarificationApplicationSession,
  userId: number,
  patientId: number,
  tSession?: TarificationSessionKey
) => async (dispatch: Dispatch<TarificationAction>) => {
  if (tSession != null && patientId > 0) {
    dispatchDeleteTarificationSessionStart()(dispatch);
    try {
      await TarificationApi.deleteTarificationSession(
        session,
        userId,
        patientId,
        tSession
      );
      dispatchDeleteTarificationSessionSuccess()(dispatch);
    } catch (e) {
      dispatchDeleteTarificationSessionError()(dispatch);
    }
  }
};

const checkOptionalETar = (
  session: TarificationApplicationSession,
  userId: number,
  attestation: Attestation,
  tSession?: TarificationSessionKey
) => async (dispatch: Dispatch<TarificationAction>) => {
  if (attestation.eFact && !!tSession) {
    await checkETarAction(
      session,
      tSession.tarificationSessionId,
      userId
    )(dispatch);
  }
};

/** ---- DISPATCHERS ---- */
const dispatchDeleteTarifiedItemSuccess = (tarifiedItemId: string) => {
  return (dispatch: Dispatch<TarificationAction>) => {
    return dispatch({
      type: DELETE_TARIFIED_ITEM_SUCCESS,
      payload: tarifiedItemId,
    });
  };
};

const dispatchDeleteTarifiedItemError = () => {
  return (dispatch: Dispatch<TarificationAction>) => {
    return dispatch({ type: DELETE_TARIFIED_ITEM_ERROR });
  };
};

const dispatchUpdateTarifiedItemStart = () => {
  return (dispatch: Dispatch<TarificationAction>) => {
    return dispatch({ type: UPDATE_TARIFIED_ITEM_START });
  };
};

const dispatchUpdateTarifiedItemSuccess = (data: TarificationSessionKey) => {
  return (dispatch: Dispatch<TarificationAction>) => {
    return dispatch({ type: UPDATE_TARIFIED_ITEM_SUCCESS, payload: data });
  };
};

const dispatchUpdateTarifiedItemError = () => {
  return (dispatch: Dispatch<TarificationAction>) => {
    return dispatch({ type: UPDATE_TARIFIED_ITEM_ERROR });
  };
};

const dispatchDeleteTarifiedItemStart = () => {
  return (dispatch: Dispatch<TarificationAction>) => {
    return dispatch({ type: DELETE_TARIFIED_ITEM_START });
  };
};

const dispatchTarificationSessionSuccess = (
  session: TarificationSessionKey
) => {
  return (dispatch: Dispatch<TarificationAction>) => {
    return dispatch({ type: TARIFICATION_SESSION_SUCCESS, payload: session });
  };
};

const dispatchTarificationSessionDetailsSuccess = (
  session: TarificationSessionKey
) => {
  return (dispatch: Dispatch<TarificationAction>) => {
    return dispatch({
      type: TARIFICATION_SESSION_DETAILS_SUCCESS,
      payload: session,
    });
  };
};

const dispatchTarificationSessionError = () => {
  return (dispatch: Dispatch<TarificationAction>) => {
    return dispatch({ type: TARIFICATION_SESSION_ERROR });
  };
};

const dispatchCheckETarStart = () => {
  return (dispatch: Dispatch<TarificationAction>) => {
    return dispatch({ type: TARIFICATION_ETAR_START });
  };
};

const dispatchCheckETarSuccess = (eTarResult: ETarResult) => {
  return (dispatch: Dispatch<TarificationAction>) => {
    return dispatch({ type: TARIFICATION_ETAR_SUCCESS, payload: eTarResult });
  };
};

const dispatchCheckETarError = () => {
  return (dispatch: Dispatch<TarificationAction>) => {
    return dispatch({ type: TARIFICATION_ETAR_ERROR });
  };
};

const dispatchDeleteTarificationSessionStart = () => {
  return (dispatch: Dispatch<TarificationAction>) => {
    return dispatch({ type: TARIFICATION_SESSION_DELETE_START });
  };
};

const dispatchDeleteTarificationSessionSuccess = () => {
  return (dispatch: Dispatch<TarificationAction>) => {
    return dispatch({ type: TARIFICATION_SESSION_DELETE_SUCCESS });
  };
};

const dispatchTarificationSessionStart = () => {
  return (dispatch: Dispatch<TarificationAction>) => {
    return dispatch({ type: TARIFICATION_SESSION_START });
  };
};

const dispatchTarificationApplicationSessionSuccess = (
  session: TarificationApplicationSession
) => {
  return (dispatch: Dispatch<TarificationAction>) => {
    return dispatch({
      type: TARIFICATION_SESSION_APP_SUCCESS,
      payload: session,
    });
  };
};

const dispatchTarificationApplicationSessionError = () => {
  return (dispatch: Dispatch<TarificationAction>) => {
    return dispatch({ type: TARIFICATION_SESSION_APP_ERROR });
  };
};

const dispatchTarificationApplicationSessionStart = () => {
  return (dispatch: Dispatch<TarificationAction>) => {
    return dispatch({ type: TARIFICATION_SESSION_APP_START });
  };
};

const dispatchFinishTarificationSessionStart = () => {
  return (dispatch: Dispatch<TarificationAction>) => {
    return dispatch({ type: FINISH_TARIFICATION_SESSION_START });
  };
};

const dispatchFinishTarificationSessionSuccess = () => {
  return (dispatch: Dispatch<TarificationAction>) => {
    return dispatch({ type: FINISH_TARIFICATION_SESSION_SUCCESS });
  };
};

const dispatchFinishTarificationSessionError = (e: any) => {
  return (dispatch: Dispatch<TarificationAction>) => {
    return dispatch({ type: FINISH_TARIFICATION_SESSION_ERROR, payload: e });
  };
};

const dispatchDeleteTarificationSessionError = () => {
  return (dispatch: Dispatch<TarificationAction>) => {
    return dispatch({ type: TARIFICATION_SESSION_DELETE_ERROR });
  };
};

const dispatchCheckTarifiedTodayStart = () => {
  return (dispatch: Dispatch<PatientAction>) => {
    return dispatch({ type: TARIFIED_TODAY_START });
  };
};

const dispatchCheckTarifiedToday = (today: boolean) => {
  return (dispatch: Dispatch<PatientAction>) => {
    return dispatch({ type: TARIFIED_TODAY, payload: today });
  };
};
